'use client';

import React from 'react';
import styles from './SearchInHero.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from "@/src/components/ui/label/Label";
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'Immersive Hero Banner with Search Bar';
const excerpt =
    'Engage your customers instantly with a hero banner featuring a built-in search bar for smooth product discovery.';

type SearchItemProps = {
    image: string;
    alt: string;
    title: string;
    text: string;
};

const items: SearchItemProps[] = [
    {
        image: '/images/themes/stockist/search-in-hero/pic-1.webp',
        alt: 'Category Preview Search',
        title: 'Category Preview Search',
        text: 'Give your customers a faster, easier way to shop with visual search that clearly shows categories and collections for quick browsing and discovery.',
    },
    {
        image: '/images/themes/stockist/search-in-hero/pic-2.webp',
        alt: 'Predictive Search',
        title: 'Predictive Search',
        text: 'Help customers find what they need faster with a predictive search that shows top search terms, trending products, and live results as they type.',
    },
    {
        image: '/images/themes/stockist/search-in-hero/pic-3.webp',
        alt: 'Search with Suggestions',
        title: 'Search with Suggestions',
        text: 'Speed up discovery with smart suggestions and instant product results — right from the first keystroke.',
    },
];

const SearchItem: React.FC<SearchItemProps> = ({ image, alt, title, text }) => (
    <div className={styles.searchInHero__item}>
        <div className={styles.searchInHero__pic}>
            <NextImage
                src={image}
                alt={alt}
                fill
                sizes="(max-width: 480px) 100vw, 33vw"
                loading="lazy"
            />
        </div>
        <h4 className={styles.searchInHero__title}>{title}</h4>
        <div className={styles.searchInHero__text}>{text}</div>
    </div>
);

const SearchInHero = () => {

    const { ref: inViewRef2, inView: inView2 } = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.searchInHero}>
            <Wrapper>
                <div className={styles.searchInHero__heading} ref={inViewRef2}>
                    <Label>Search in Hero</Label>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>

                <div className={styles.searchInHero__video}>
                    <NextImage
                        src="/images/themes/stockist/search-in-hero/video.webp"
                        alt="PreviewVideo"
                        fill
                        sizes="(max-width: 480px) 100vw, (max-width: 1280px) 60vw, 100vw"
                        loading="lazy"
                    />
                </div>

                <div className={styles.searchInHero__grid}>
                    {items.map((item, index) => (
                        <SearchItem key={index} {...item} />
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default SearchInHero;
